import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaEye, FaPen, FaFilePdf, FaClipboardList, FaTimes } from "react-icons/fa";
import * as api from "../api/placementApi";
import {
  T, OUTCOME_OPTIONS, INTERVIEW_PARAMETERS, GlobalStyles, Spinner, PillSelect, StagePill, StarRating,
  overlayStyle, modalStyle, primaryBtnStyle, textBtnStyle, inputStyle, labelStyle,
} from "./uiKit";

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

const RESULT_WINDOW_HOURS = 3;

// View / Resume / Edit-Result access is all time-boxed to the scheduled
// slot: locked before it starts (nothing to view/log yet), open from the
// scheduled time through RESULT_WINDOW_HOURS after it, then locked again —
// viewing or logging a result long after the fact goes through the admin
// Recruitment page instead. Entries with no scheduledDate at all (legacy
// data) fail open rather than being permanently stuck.
function getResultWindowStatus(scheduledDate) {
  if (!scheduledDate) return { enabled: true, reason: "" };
  const start = new Date(scheduledDate).getTime();
  const end = start + RESULT_WINDOW_HOURS * 60 * 60 * 1000;
  const now = Date.now();
  if (now < start) {
    return { enabled: false, reason: `Opens at ${formatDateTime(scheduledDate)}` };
  }
  if (now > end) {
    return { enabled: false, reason: `Window closed ${RESULT_WINDOW_HOURS}h after the scheduled time` };
  }
  return { enabled: true, reason: "" };
}

const OUTCOME_STYLE = {
  PASSED: { bg: T.greenSoft, fg: T.green, label: "Passed" },
  FAILED: { bg: T.dangerSoft, fg: T.danger, label: "Failed" },
  ON_HOLD: { bg: T.amberSoft, fg: "#b45309", label: "On Hold" },
};

function OutcomePill({ outcome }) {
  const s = OUTCOME_STYLE[outcome];
  if (!s) return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: "#f1f5f9", color: T.ink3 }}>Pending</span>;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11.5,
      fontWeight: 700, letterSpacing: ".2px", background: s.bg, color: s.fg, fontFamily: T.fontBody,
    }}>
      {s.label}
    </span>
  );
}

const theadCellStyle = {
  textAlign: "left", padding: "11px 16px", fontSize: 11.5, fontWeight: 700, color: T.ink3,
  textTransform: "uppercase", letterSpacing: ".4px", fontFamily: T.fontDisplay,
  position: "sticky", top: 0, background: "#fafbfc", zIndex: 1,
};
const tdCellStyle = {
  padding: "13px 16px", fontSize: 13.5, color: T.ink2, verticalAlign: "middle",
};
const iconBtnStyle = {
  border: "none", background: "transparent", color: T.ink2, cursor: "pointer",
  fontSize: 13, padding: 7, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
};

/* ── Resume viewer modal ── same "fetch as Blob, embed via object URL"
   approach as the admin Recruitment page's ResumeViewerModal (auth token
   can't be attached to a plain <iframe src>). Kept as a local copy rather
   than importing from PlacementAdminPage.js, which doesn't export it. ── */
function ResumeViewerModal({ candidateId, fileName, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let objUrl = null;
    setLoading(true);
    setError(false);
    api.fetchResumeBlob(candidateId)
      .then((blob) => {
        if (!active) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [candidateId]);

  return (
    <div style={{ ...overlayStyle, zIndex: 1200 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "min(920px, 94vw)", height: "92vh", background: T.white, borderRadius: T.radius,
        boxShadow: T.shadowMd, display: "flex", flexDirection: "column", overflow: "hidden",
        fontFamily: T.fontBody, animation: "plc-fade-in .16s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
            <FaFilePdf color={T.danger} size={15} />
            <span style={{ fontSize: 14, fontWeight: 600, color: T.ink1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {fileName || "Resume"}
            </span>
          </div>
          <button type="button" onClick={onClose} title="Close" style={iconBtnStyle}>
            <FaTimes size={15} />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: "#525659" }}>
          {loading ? (
            <Spinner padding="160px 0" />
          ) : error ? (
            <div style={{ color: "#fff", textAlign: "center", padding: 60, fontSize: 13.5 }}>Failed to load resume.</div>
          ) : (
            <iframe src={blobUrl} title="Resume Preview" style={{ width: "100%", height: "100%", border: "none" }} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── View Result modal ── read-only: name + result details (score,
   outcome, feedback) only — remarks are deliberately left out here, since
   this view is meant to show the CANDIDATE's result, not internal/logistics
   notes. Editing (which does include remarks) is a separate action. ── */
function ViewResultModal({ entry, onClose }) {
  const stageLabel = entry.stage === "TEST" ? "Test" : "Interview";
  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 440, maxHeight: "88vh", overflowY: "auto" }} className="plc-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>{stageLabel} Result</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 18 }}>
          {entry.positionApplied || "Role not specified"}{entry.roundLabel ? ` · ${entry.roundLabel}` : ""}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Name</label>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: T.ink1 }}>{entry.candidateName}</div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Score / Rating</label>
          <div style={{ fontSize: 13.5, color: T.ink1 }}>{entry.score || "—"}</div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Outcome</label>
          <div><OutcomePill outcome={entry.outcome} /></div>
        </div>

        <div style={{ marginBottom: entry.stage === "INTERVIEW" ? 14 : 6 }}>
          <label style={labelStyle}>Feedback</label>
          <div style={{ fontSize: 13.5, color: T.ink1, whiteSpace: "pre-wrap" }}>{entry.feedback || "—"}</div>
        </div>

        {entry.stage === "INTERVIEW" && (
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>Scorecard</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {INTERVIEW_PARAMETERS.map((p) => (
                <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: T.ink2 }}>{p.label}</span>
                  <StarRating value={entry.evaluation?.[p.key]} readOnly size={14} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Add/Edit Result modal ── score / outcome / feedback only — deliberately
   NO remarks field here (unlike the admin Recruitment page's LogResultModal):
   remarks are internal/logistics notes, out of scope for this self-service
   page. Not sending "remarks" in the update payload at all (rather than
   sending it as null) means any remarks already set elsewhere are left
   untouched. ── */
function AddResultModal({ entry, onClose, onDone }) {
  const [score, setScore] = useState(entry.score || "");
  const [outcome, setOutcome] = useState(entry.outcome || "");
  const [feedback, setFeedback] = useState(entry.feedback || "");
  const [ratings, setRatings] = useState(() => {
    const r = {};
    INTERVIEW_PARAMETERS.forEach((p) => { r[p.key] = entry.evaluation?.[p.key] ?? null; });
    return r;
  });
  const [saving, setSaving] = useState(false);

  const isInterview = entry.stage === "INTERVIEW";
  const setRating = (key) => (v) => setRatings((r) => ({ ...r, [key]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateStageEntry(entry.candidateId, entry.historyId, {
        score: score || null, outcome: outcome || null, feedback: feedback || null,
        ...(isInterview ? ratings : {}),
      });
      toast.success("Result saved");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to save result");
    } finally {
      setSaving(false);
    }
  };

  const stageLabel = entry.stage === "TEST" ? "Test" : "Interview";

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 440, maxHeight: "88vh", overflowY: "auto" }} className="plc-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>
            {entry.pending ? `Add ${stageLabel} Result` : `Edit ${stageLabel} Result`}
          </span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 18 }}>
          {entry.positionApplied || "Role not specified"}{entry.roundLabel ? ` · ${entry.roundLabel}` : ""}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Score / Rating</label>
          <input style={inputStyle} value={score} onChange={(e) => setScore(e.target.value)} placeholder="e.g. 78/100 or 4/5" />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Outcome</label>
          <PillSelect value={outcome} options={[{ value: "", label: "Not decided yet" }, ...OUTCOME_OPTIONS]} onChange={setOutcome} placeholder="Not decided yet" />
        </div>

        <div style={{ marginBottom: isInterview ? 14 : 6 }}>
          <label style={labelStyle}>Feedback</label>
          <textarea rows={4} style={{ ...inputStyle, resize: "vertical" }} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Your write-up on the candidate's performance…" />
        </div>

        {isInterview && (
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>Scorecard</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {INTERVIEW_PARAMETERS.map((p) => (
                <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: T.ink2 }}>{p.label}</span>
                  <StarRating value={ratings[p.key]} onChange={setRating(p.key)} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.55 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Navbar-agnostic content — Admin/Manager/Employee wrapper pages each embed
// this with their own navbar (same pattern as PlacementAdminPage), since
// anyone can be assigned as a tester/interviewer regardless of role.
const TABS = [
  { value: "TEST", label: "Test" },
  { value: "INTERVIEW", label: "Interview" },
];

export default function MyInterviewsPage() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("TEST");
  const [editTarget, setEditTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [resumeTarget, setResumeTarget] = useState(null); // { candidateId, fileName }

  const load = () => {
    const employeeId = localStorage.getItem("employeeId");
    if (!employeeId) { setLoading(false); return; }
    setLoading(true);
    api.getMyAssignments(employeeId)
      .then((list) => setAssignments(list || []))
      .catch(() => toast.error("Failed to load your assignments"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Resume is only shown on the Interview tab — a Test round has no resume
  // decision to make at that point, the resume already mattered upstream
  // (Screening); by Interview it's the more relevant reference.
  const showResumeColumn = activeTab === "INTERVIEW";
  const filtered = assignments.filter((a) => a.stage === activeTab);
  const testCount = assignments.filter((a) => a.stage === "TEST").length;
  const interviewCount = assignments.filter((a) => a.stage === "INTERVIEW").length;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bg }}>
      <GlobalStyles />
      <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px" }} className="plc-scroll">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: T.primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FaClipboardList size={15} color="#fff" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 21, color: T.ink1, fontWeight: 700, fontFamily: T.fontDisplay }}>Recruitment Panel</h2>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>Tests / interviews you've been assigned to conduct</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 18, borderBottom: `1px solid ${T.border}` }}>
          {TABS.map((t) => {
            const count = t.value === "TEST" ? testCount : interviewCount;
            const active = activeTab === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setActiveTab(t.value)}
                style={{
                  border: "none", background: "transparent", cursor: "pointer",
                  padding: "10px 4px", marginBottom: -1, fontFamily: T.fontBody,
                  fontSize: 13.5, fontWeight: 700, color: active ? T.primary : T.ink3,
                  borderBottom: active ? `2px solid ${T.primary}` : "2px solid transparent",
                  display: "flex", alignItems: "center", gap: 7,
                }}
              >
                {t.label}
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20,
                  background: active ? T.primarySoft : "#f1f5f9", color: active ? T.primary : T.ink3,
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <Spinner padding="80px 0" />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: T.ink3 }}>
            <FaClipboardList size={28} style={{ marginBottom: 10, opacity: 0.5 }} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>No {activeTab === "TEST" ? "tests" : "interviews"} yet</div>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>You'll see it here when you're assigned to conduct one.</div>
          </div>
        ) : (
          <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...theadCellStyle, width: 56 }}>Sr.No</th>
                  <th style={theadCellStyle}>Name</th>
                  <th style={theadCellStyle}>Position Applied</th>
                  <th style={theadCellStyle}>Stage</th>
                  <th style={theadCellStyle}>Result</th>
                  {showResumeColumn && <th style={theadCellStyle}>Resume</th>}
                  <th style={theadCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, idx) => {
                  const windowStatus = getResultWindowStatus(a.scheduledDate);
                  const disabledBtnStyle = { ...iconBtnStyle, color: T.ink3, cursor: "not-allowed", opacity: 0.4 };
                  return (
                    <tr key={a.historyId} style={{ borderTop: `1px solid ${T.border}` }}>
                      <td style={{ ...tdCellStyle, color: T.ink3, fontFamily: T.fontMono, fontSize: 12 }}>{idx + 1}</td>
                      <td style={{ ...tdCellStyle, fontWeight: 600, color: T.ink1 }}>{a.candidateName}</td>
                      <td style={tdCellStyle}>{a.positionApplied || "—"}</td>
                      <td style={tdCellStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                          <StagePill stage={a.stage} />
                          {a.roundLabel && <span style={{ fontSize: 12, color: T.ink2 }}>{a.roundLabel}</span>}
                        </div>
                        <div style={{ fontSize: 11.5, color: T.ink3, fontFamily: T.fontMono, marginTop: 4 }}>{formatDateTime(a.scheduledDate)}</div>
                      </td>
                      <td style={tdCellStyle}><OutcomePill outcome={a.outcome} /></td>
                      {showResumeColumn && (
                        <td style={tdCellStyle}>
                          {a.hasResume ? (
                            <button
                              type="button"
                              disabled={!windowStatus.enabled}
                              onClick={() => windowStatus.enabled && setResumeTarget({ candidateId: a.candidateId, fileName: a.resumeOriginalFileName })}
                              title={windowStatus.enabled ? "View resume" : windowStatus.reason}
                              style={windowStatus.enabled ? iconBtnStyle : disabledBtnStyle}
                            >
                              <FaFilePdf size={13} color={windowStatus.enabled ? T.danger : T.ink3} />
                            </button>
                          ) : (
                            <span style={{ color: T.ink3 }}>—</span>
                          )}
                        </td>
                      )}
                      <td style={tdCellStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <button
                            type="button"
                            disabled={!windowStatus.enabled}
                            onClick={() => windowStatus.enabled && setViewTarget(a)}
                            title={windowStatus.enabled ? "View" : windowStatus.reason}
                            style={windowStatus.enabled ? iconBtnStyle : disabledBtnStyle}
                          >
                            <FaEye size={13} />
                          </button>
                          <button
                            type="button"
                            disabled={!windowStatus.enabled}
                            onClick={() => windowStatus.enabled && setEditTarget(a)}
                            title={windowStatus.enabled ? (a.pending ? "Add Result" : "Edit Result") : windowStatus.reason}
                            style={windowStatus.enabled ? iconBtnStyle : disabledBtnStyle}
                          >
                            <FaPen size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewTarget && (
        <ViewResultModal entry={viewTarget} onClose={() => setViewTarget(null)} />
      )}
      {editTarget && (
        <AddResultModal entry={editTarget} onClose={() => setEditTarget(null)} onDone={load} />
      )}
      {resumeTarget && (
        <ResumeViewerModal candidateId={resumeTarget.candidateId} fileName={resumeTarget.fileName} onClose={() => setResumeTarget(null)} />
      )}
    </div>
  );
}
